import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import {inject} from "@angular/core";
import {ToastService} from "../services/toast.service";
import {catchError} from "rxjs/operators";
import {throwError} from 'rxjs';
import {UserDataService} from "../services/user-data.service";
import {Router} from "@angular/router";

/**
 * Token для отключения авто-тоста на конкретный запрос.
 * Использование: this.http.get(url, { context: new HttpContext().set(SKIP_ERROR_TOAST, true) })
 */
export const SKIP_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

/**
 * Извлекает осмысленное сообщение об ошибке из разных форматов ответа бэка:
 *   { messages: ["Invalid credentials"] }   ← основной формат IQRA
 *   { message: "..." }                      ← классический Spring
 *   { error: "..." }                        ← запасной
 *   "raw string"                            ← если backend не JSON
 */
export function extractErrorMessage(err: HttpErrorResponse): string | undefined {
  const body = err?.error;
  if (!body) return undefined;

  if (typeof body === 'string') return body;

  if (Array.isArray(body.messages) && body.messages.length > 0) {
    return body.messages.join('\n');
  }

  if (typeof body.message === 'string') return body.message;
  if (typeof body.error === 'string')   return body.error;

  return undefined;
}

/** Дефолтный текст по HTTP-коду, если backend ничего не вернул. */
function defaultMessageForStatus(status: number): string {
  switch (status) {
    case 0:   return 'Нет связи с сервером. Проверьте интернет.';
    case 400: return 'Неверный запрос';
    case 401: return 'Сессия истекла. Войдите снова.';
    case 403: return 'Недостаточно прав';
    case 404: return 'Не найдено';
    case 409: return 'Конфликт данных';
    case 422: return 'Невалидные данные';
    case 500: return 'Внутренняя ошибка сервера';
    case 502:
    case 503:
    case 504: return 'Сервер временно недоступен';
    default:  return `Ошибка (${status})`;
  }
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);
  const userDataService = inject(UserDataService);
  const router = inject(Router);

  const skipToast = req.context.get(SKIP_ERROR_TOAST);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      // 1) Нормализуем тело: гарантируем `err.error.message`, чтобы
      // существующий код в компонентах с `err?.error?.message` сработал.
      const extracted = extractErrorMessage(err);
      try {
        if (extracted) {
          if (err.error && typeof err.error === 'object') {
            if (!err.error.message) err.error.message = extracted;
          } else {
            (err as any).error = {message: extracted, raw: err.error};
          }
        }
      } catch { /* readonly safety */ }

      // 2) Спец-кейсы
      if (err.status === 401) {
        // 401 → редирект на логин; на странице логина показываем токст
        // (там как раз пользователь ждёт обратной связи), на остальных — нет.
        const onLogin = router.url.startsWith('/auth');
        if (onLogin && !skipToast) {
          toastService.showError(extracted ?? defaultMessageForStatus(401));
        } else {
          userDataService.logout();
          router.navigateByUrl('/auth/login');
        }
        return throwError(() => err);
      }

      // 3) Остальные коды — глобальный тост, если не отключено флагом
      if (!skipToast) {
        const message = extracted ?? defaultMessageForStatus(err.status);
        toastService.showError(message);
      }

      return throwError(() => err);
    })
  );
};
